import { useState } from "react";
import { STATUS, DEFAULT_STATUSES, PriorityFlag, Avatar } from "./pm";
import { CalendarBlank, ChatCircleText, ListChecks } from "@phosphor-icons/react";

export function KanbanView({ tasks, onStatusChange, onOpen, statuses }) {
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);
  const ord = statuses && statuses.length ? statuses : DEFAULT_STATUSES;
  const sm = Object.fromEntries(ord.map((s) => [s.key, s]));
  const byStatus = {};
  ord.forEach((s) => (byStatus[s.key] = []));
  tasks.forEach((t) => (byStatus[t.status] || byStatus[ord[0].key]).push(t));

  return (
    <div className="flex gap-3 overflow-x-auto pb-3" data-testid="kanban-view">
      {ord.map((col) => {
        const s = col.key;
        const list = byStatus[s] || [];
        return (
          <div key={s} data-testid={`kanban-col-${s}`}
            onDragOver={(e) => { e.preventDefault(); setOver(s); }}
            onDragLeave={() => setOver((o) => (o === s ? null : o))}
            onDrop={() => { if (dragId) onStatusChange(dragId, s); setDragId(null); setOver(null); }}
            className={`w-72 shrink-0 rounded-lg border transition-colors duration-150 ${over === s ? "border-primary bg-primary/5" : "border-border bg-neutral-50/60"}`}>
            <div className="flex items-center justify-between px-3 h-10 border-b border-border">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: col.color }}><span className="w-2 h-2 rounded-full" style={{ background: col.color }} />{col.label}</span>
              <span className="text-xs text-neutral-400">{list.length}</span>
            </div>
            <div className="p-2 space-y-2 min-h-[80px] max-h-[calc(100vh-320px)] overflow-y-auto">
              {list.map((t) => (
                <div key={t.id} draggable onDragStart={() => setDragId(t.id)} onDragEnd={() => { setDragId(null); setOver(null); }}
                  onClick={() => onOpen(t.id)} data-testid={`kanban-card-${t.id}`}
                  className="bg-white rounded-md border border-border p-2.5 cursor-pointer hover:shadow-md hover:border-neutral-300 transition-all duration-150 active:cursor-grabbing">
                  <p className={`text-sm mb-2 ${sm[t.status]?.done ? "line-through text-neutral-400" : "text-neutral-800"}`}>{t.title}</p>
                  <div className="flex items-center justify-between">
                    <PriorityFlag value={t.priority} />
                    <div className="flex items-center gap-2 text-[11px] text-neutral-400">
                      {t.subtask_total > 0 && <span className="inline-flex items-center gap-0.5"><ListChecks size={12} />{t.subtask_done}/{t.subtask_total}</span>}
                      {(t.comments || []).length > 0 && <span className="inline-flex items-center gap-0.5"><ChatCircleText size={12} />{(t.comments || []).length}</span>}
                      {t.due_date && <span className="inline-flex items-center gap-0.5"><CalendarBlank size={12} />{new Date(t.due_date + "T00:00:00").toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}</span>}
                      <span className="flex -space-x-1.5">{(t.assignee_ids || []).slice(0, 2).map((id, i) => <Avatar key={`${id}-${i}`} id={id} name={(t.assignee_names || [])[i]} size={20} />)}</span>
                    </div>
                  </div>
                </div>
              ))}
              {list.length === 0 && <p className="text-xs text-neutral-300 text-center py-4">Suelta aquí</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function TimelineView({ tasks, onOpen, statuses }) {
  const sm = Object.fromEntries((statuses && statuses.length ? statuses : DEFAULT_STATUSES).map((s) => [s.key, s]));
  const withDates = tasks.filter((t) => t.due_date || t.start_date).map((t) => ({ ...t, s: t.start_date || t.due_date, e: t.due_date || t.start_date }));
  if (!withDates.length) return <div className="p-10 text-center text-neutral-400 text-sm border border-border rounded-lg bg-white" data-testid="timeline-view">No hay tareas con fechas para mostrar en la línea de tiempo.</div>;
  const min = withDates.reduce((m, t) => (t.s < m ? t.s : m), withDates[0].s);
  const max = withDates.reduce((m, t) => (t.e > m ? t.e : m), withDates[0].e);
  const d0 = new Date(min + "T00:00:00"), d1 = new Date(max + "T00:00:00");
  const total = Math.max(1, (d1 - d0) / 86400000 + 1);
  const off = (d) => ((new Date(d + "T00:00:00") - d0) / 86400000);
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <div className="border border-border rounded-lg bg-white overflow-x-auto" data-testid="timeline-view">
      <div className="min-w-[640px] p-4 space-y-2 relative">
        <div className="flex justify-between text-[11px] text-neutral-400 mb-2 font-medium">
          <span>{d0.toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}</span>
          <span>{d1.toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}</span>
        </div>
        {todayIso >= min && todayIso <= max && <div className="absolute top-8 bottom-2 w-px bg-red-400/60 z-10" style={{ left: `calc(1rem + ${(off(todayIso) / total) * 100}%)` }} />}
        {withDates.sort((a, b) => a.s.localeCompare(b.s)).map((t) => {
          const col = sm[t.status] || DEFAULT_STATUSES[0];
          const left = (off(t.s) / total) * 100;
          const width = Math.max(2, ((off(t.e) - off(t.s) + 1) / total) * 100);
          return (
            <div key={t.id} className="flex items-center gap-2 group">
              <span className="w-40 shrink-0 text-xs truncate text-neutral-600">{t.title}</span>
              <div className="flex-1 relative h-6">
                <button onClick={() => onOpen(t.id)} data-testid={`timeline-bar-${t.id}`} title={`${t.s} → ${t.e}`} className="absolute top-0.5 h-5 rounded-md hover:opacity-80 transition-opacity flex items-center px-1.5 overflow-hidden" style={{ left: `${left}%`, width: `${width}%`, background: `${col.color}1A`, border: `1px solid ${col.color}44` }}>
                  <span className="text-[10px] font-semibold truncate" style={{ color: col.color }}>{Math.round(t.subtask_total ? t.subtask_pct : (col.done ? 100 : (t.progress || 0)))}%</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function CalendarView({ tasks, onOpen, month, setMonth, statuses }) {
  const sm = Object.fromEntries((statuses && statuses.length ? statuses : DEFAULT_STATUSES).map((s) => [s.key, s]));
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const startDay = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const iso = (d) => `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayIso = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; })();
  const tasksOn = (d) => tasks.filter((t) => t.due_date === iso(d));

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="cal-prev" className="h-8 px-3 rounded-md border border-input text-sm hover:bg-neutral-50">‹ Anterior</button>
        <h3 className="text-lg font-semibold capitalize" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{month.toLocaleDateString("es-MX", { month: "long", year: "numeric" })}</h3>
        <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="cal-next" className="h-8 px-3 rounded-md border border-input text-sm hover:bg-neutral-50">Siguiente ›</button>
      </div>
      <div className="grid grid-cols-7 gap-px bg-border rounded-lg overflow-hidden border border-border">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => <div key={d} className="bg-neutral-50 text-center text-xs font-semibold uppercase tracking-[0.05em] text-neutral-500 py-2">{d}</div>)}
        {cells.map((d, i) => (
          <div key={i} className={`bg-white min-h-[96px] p-1.5 ${d === null ? "bg-neutral-50/50" : ""}`}>
            {d && (
              <>
                <span className={`inline-flex items-center justify-center w-6 h-6 text-xs rounded-full mb-1 ${iso(d) === todayIso ? "bg-primary text-white font-bold" : "text-neutral-500"}`}>{d}</span>
                <div className="space-y-1">
                  {tasksOn(d).slice(0, 3).map((t) => (
                    <button key={t.id} onClick={() => onOpen(t.id)} data-testid={`cal-task-${t.id}`} className="w-full text-left text-[11px] truncate rounded px-1.5 py-0.5 font-medium hover:opacity-80 transition-opacity" style={{ background: `${(sm[t.status] || {}).color || "#6B7280"}1A`, color: (sm[t.status] || {}).color || "#6B7280" }}>{t.title}</button>
                  ))}
                  {tasksOn(d).length > 3 && <span className="text-[10px] text-neutral-400 px-1">+{tasksOn(d).length - 3} más</span>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
